import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import FormLabel from '@mui/material/FormLabel';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { FieldError } from '@/ui';
import { Field } from 'redux-form';
import { FixedSizeList } from 'react-window';
import debounce from 'lodash/debounce';
import SearchLabel from '../../../../components/SearchLabel/SearchLabel';
import DraggableOffer from '../DraggableOffer';
import ZuulLoader from '../../../../components/ZuulLoader/ZuulLoader';
import { DESTINATION_OFFER_TYPE_NAME } from '../constants';
import './LeftAside.css';

const LABEL_SX = { display: 'block', mb: 0.5 };

const TABS = ['Offers', 'Blocks', 'Dynamic', 'Destinations'];

const NameField = memo(function NameField({ input, disabled, type, placeholder, meta: { touched, error } }) {
  return (
    <>
      <TextField
        {...input}
        id='sequenceName'
        disabled={disabled}
        placeholder={placeholder}
        type={type}
        error={Boolean(touched && error)}
      />
      <FieldError error={error} touched={touched} />
    </>
  );
});

const VirtualRow = memo(function VirtualRow({ index, style, data }) {
  const offer = data[index];

  if (!offer) {
    return <div style={style}>Loading...</div>;
  }

  return (
    <div style={style}>
      <div
        style={{
          height: '65px',
          paddingBottom: '5px',
          overflow: 'hidden',
        }}
      >
        <DraggableOffer offer={offer} />
      </div>
    </div>
  );
});

const LeftAside = memo(function LeftAside({ offers, areOffersLoading }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('Offers');
  const [listHeight, setListHeight] = useState(0);
  const poolRef = useRef(null);
  const listRef = useRef(null);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 300),
    []
  );

  // The virtual list needs an explicit pixel height, and it has to be exactly the pool's
  // content box. react-window decides whether to show a scrollbar by comparing the rows'
  // total height against this number, so a list taller than the pool is the worst case:
  // the pool (overflow: hidden) clips the surplus, and when the rows fit inside the
  // list's own viewport there is no scrollbar to reach them with. A floor of 300px used
  // to guarantee exactly that whenever the aside was shorter than ~320px — a short
  // browser window hid the last rows of a short list with no way to scroll to them.
  //
  // A ResizeObserver, not a window resize listener: the pool also changes height on its
  // own — a validation message appearing under the sequence name, the tab strip wrapping,
  // the loader giving way to the list — and none of those fire a window resize.
  useLayoutEffect(() => {
    const pool = poolRef.current;
    if (!pool) return undefined;

    const measure = () => {
      const { paddingTop, paddingBottom } = window.getComputedStyle(pool);
      const padding = parseFloat(paddingTop) + parseFloat(paddingBottom);
      setListHeight(Math.max(0, pool.clientHeight - padding));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(pool);
    return () => observer.disconnect();
  }, []);

  // A re-filtered list starts at the top. react-window keeps its scroll offset when the
  // rows underneath it change, so switching tab or typing a search used to drop you
  // part-way down a list whose beginning you had never seen.
  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [activeTab, debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const draggableOffers = useMemo(() => {
    if (!offers || offers.length === 0) return [];

    const searchLower = debouncedSearchTerm.toLowerCase();

    return offers.filter((offer) => {
      if (!offer || !offer.offerType) return false;

      const isOfferBlock = offer.offerType.name === 'Offer Block';
      const isDynamicOffer = offer.offerType.name === 'Dynamic Offer Section';
      // ZET-4795: hidden wrapper offers for linkout-family offer actions (Destinations,
      // Linkout Waterfalls, Destination Waterfalls). They get their own tab so they never
      // mix into the Offers pool.
      const isDestination = offer.offerType.name === DESTINATION_OFFER_TYPE_NAME;
      const isPlainOffer = !isOfferBlock && !isDynamicOffer && !isDestination;

      if (!debouncedSearchTerm) {
        return (
          (activeTab === 'Offers' && isPlainOffer) ||
          (activeTab === 'Blocks' && isOfferBlock) ||
          (activeTab === 'Dynamic' && isDynamicOffer) ||
          (activeTab === 'Destinations' && isDestination)
        );
      }
      const matchesSearch =
        offer.name?.toLowerCase().includes(searchLower) ||
        offer.id?.toString().includes(debouncedSearchTerm) ||
        offer.offerBlock?.id?.toString().includes(debouncedSearchTerm) ||
        offer?.dynamicOfferSection?.id?.toString().includes(debouncedSearchTerm) ||
        // ZET-4795: let operators find a Destination by its offer action id
        offer?.offerActionId?.toString().includes(debouncedSearchTerm);

      return (
        (activeTab === 'Offers' && isPlainOffer && matchesSearch) ||
        (activeTab === 'Blocks' && isOfferBlock && matchesSearch) ||
        (activeTab === 'Dynamic' && isDynamicOffer && matchesSearch) ||
        (activeTab === 'Destinations' && isDestination && matchesSearch)
      );
    });
  }, [offers, debouncedSearchTerm, activeTab]);

  return (
    <div className='left-aside-container'>
      <div>
        <FormLabel htmlFor='sequenceName' sx={LABEL_SX}>
          Sequence Name
        </FormLabel>
        <Field required type='text' component={NameField} name='sequenceName' placeholder='Sequence Name' />
      </div>
      <div>
        <SearchLabel />
        <TextField
          type='text'
          placeholder='Use spaces to separate search terms'
          onChange={handleSearchChange}
          value={searchTerm}
        />
      </div>
      {/* Four labels do not fit this aside's width, so they scroll rather than being squeezed
          into each other. */}
      <Tabs
        value={activeTab}
        onChange={(_event, next) => setActiveTab(next)}
        variant='scrollable'
        scrollButtons='auto'
        sx={{
          minHeight: 36,
          flexShrink: 0,
          '& .MuiTab-root': { minHeight: 36, minWidth: 0, px: 1, fontSize: '0.75rem' },
        }}
      >
        {TABS.map((tab) => (
          <Tab key={tab} value={tab} label={tab} />
        ))}
      </Tabs>
      <div className='draggableOffersPool' ref={poolRef}>
          {areOffersLoading ? (
            <ZuulLoader text={'Loading Offers...'} />
          ) : draggableOffers.length > 0 ? (
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              itemCount={draggableOffers.length}
              itemSize={70}
              width='100%'
              overscanCount={5}
              itemData={draggableOffers}
            >
              {VirtualRow}
            </FixedSizeList>
          ) : (
          <div style={{ padding: '20px', textAlign: 'center' }}>No {activeTab.toLowerCase()} found</div>
        )}
      </div>
    </div>
  );
});
export default LeftAside;
